import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Flame, Brain, Lock, ChevronDown, Star, Crown, Sparkles, Trophy, Target } from 'lucide-react';
import Footer, { StoreButtons } from '../components/Footer';
import Logo, { LogoMark } from '../components/Logo';
import { PhoneFrame, RealScreen } from '../components/PhoneMockups';

/* ──────────────  scroll-reveal  ────────────── */
function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) { setVisible(true); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }),
      { threshold, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms`, transitionDuration: '900ms', transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)' }}
      className={`transition-[opacity,transform] will-change-[opacity,transform] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
    >
      {children}
    </div>
  );
}

/* ──────────────  data  ────────────── */
const FAQ = [
  { q: 'What is ResetProtocol?', a: 'ResetProtocol is a discreet recovery support app for men building discipline and breaking unwanted habits. It combines streak tracking, urge support, an AI coach, and a gamified ranking system into a single private companion.' },
  { q: 'Is this a medical or therapy service?', a: 'No. ResetProtocol is a wellness and self-improvement tool — not a substitute for medical advice, diagnosis, or treatment. If you are in crisis, please consult a qualified professional.' },
  { q: 'Is my data private?', a: 'Yes. We never sell your personal data. Your check-ins, journal entries, and AI conversations are tied to your account and used only to personalize your experience. See our Privacy Policy for full details.' },
  { q: 'How much does it cost?', a: 'A 7-day free trial, then a low monthly subscription billed through your Apple ID or Google Play account. Cancel anytime in your device settings.' },
  { q: 'How do I cancel my subscription?', a: 'iOS: Settings → [your name] → Subscriptions. Android: Google Play → Profile → Payments & subscriptions. Cancellation is handled by Apple and Google — we do not bill you outside those stores.' },
  { q: 'How do I delete my account?', a: 'In-app: Settings → Delete Account. Or email support@resetprotocol.app from your registered address — we erase your account data within 30 days.' },
];

const TESTIMONIALS = [
  { q: '47 days strong. Longest streak of my adult life. The Content Shield made it real.', n: 'Marcus, 28' },
  { q: 'The coach is there at 2am when nothing else is. No judgment, just direction.', n: 'D., 34' },
  { q: 'I sleep better. I lift again. This app forced honesty with myself.', n: 'James, 22' },
];

const FEATURES = [
  { icon: Flame, title: 'Streak system', desc: 'Daily check-ins, level ladder, milestone unlocks. Watch your discipline stack up.' },
  { icon: Shield, title: 'Content Shield', desc: 'Optional device-level distraction filtering with verifiable status — no manual setup.' },
  { icon: Brain, title: 'AI Coach', desc: 'A 24/7 ally for accountability and habit improvement. Honest, never preachy.' },
  { icon: Lock, title: 'Anonymous', desc: 'Pick a warrior name. No real identity. No judgment. Just the protocol.' },
  { icon: Trophy, title: 'Ranks & XP', desc: '13 titles from Initiate to Ascended. Earn XP every clean day you stay focused.' },
  { icon: Target, title: 'Urge Control', desc: 'One-tap support when temptation hits. Breathwork, redirection, and your coach.' },
];

/* ──────────────  components  ────────────── */
function FeatureCard({ icon: Icon, title, desc, idx }) {
  return (
    <Reveal delay={idx * 60}>
      <div className="group relative p-6 bg-gradient-to-br from-white/[0.025] to-transparent border border-white/[0.06] hover:border-[#a83a4a]/30 transition-all duration-500 overflow-hidden h-full">
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#a83a4a]/0 group-hover:bg-[#a83a4a]/10 rounded-full blur-2xl transition-all duration-700" />
        <div className="relative">
          <div className="w-10 h-10 mb-5 flex items-center justify-center border border-[#a83a4a]/30 bg-gradient-to-br from-[#3a0e15]/40 to-transparent">
            <Icon className="w-4 h-4 text-[#a83a4a]" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-base text-white mb-2 tracking-tight">{title}</h3>
          <p className="text-white/40 text-sm font-body leading-relaxed">{desc}</p>
        </div>
      </div>
    </Reveal>
  );
}

function FaqItem({ q, a, open, onToggle, idx }) {
  return (
    <div className={`border transition-all duration-300 ${open ? 'border-[#a83a4a]/30 bg-gradient-to-br from-[#3a0e15]/15 to-transparent' : 'border-white/[0.06] bg-white/[0.015]'}`}>
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/[0.02] transition"
        data-testid={`faq-q-${idx}`}
      >
        <span className="font-heading text-sm md:text-base text-white pr-4 tracking-tight">{q}</span>
        <ChevronDown className={`w-4 h-4 text-[#a83a4a] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-500 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-white/55 font-body text-sm leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

/* ──────────────  page  ────────────── */
export default function Landing() {
  const [openFaq, setOpenFaq] = useState(null);
  const [scroll, setScroll] = useState(0);

  useEffect(() => {
    const onScroll = () => setScroll(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#020202] text-white overflow-x-hidden">
      {/* Cinematic ambient background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(ellipse at top, rgba(122,31,43,0.18) 0%, transparent 55%)' }} />
        <div className="absolute top-[40%] left-[-10%] w-[500px] h-[500px] bg-[#7a1f2b]/8 rounded-full blur-[160px]" style={{ transform: `translateY(${scroll * 0.15}px)` }} />
        <div className="absolute top-[80%] right-[-10%] w-[400px] h-[400px] bg-[#a83a4a]/8 rounded-full blur-[140px]" style={{ transform: `translateY(${scroll * -0.1}px)` }} />
        <div
          className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
          style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")' }}
        />
      </div>

      {/* NAV */}
      <nav className="relative z-30 border-b border-white/[0.06] backdrop-blur-xl bg-[#020202]/80">
        <div className="container mx-auto max-w-6xl px-5 sm:px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo">
            <LogoMark size={32} />
          </Link>
          <div className="flex gap-3 sm:gap-7 text-[10px] sm:text-[11px] font-body uppercase tracking-[0.18em] sm:tracking-[0.2em] text-white/50">
            <Link to="/privacy" className="hover:text-white transition" data-testid="nav-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition" data-testid="nav-terms">Terms</Link>
            <Link to="/support" className="hover:text-white transition" data-testid="nav-support">Support</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 container mx-auto max-w-6xl px-6 pt-12 md:pt-20 pb-16 md:pb-28">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#a83a4a]/30 bg-gradient-to-r from-[#3a0e15]/40 to-transparent mb-7 animate-[fadeUp_0.9s_cubic-bezier(0.16,1,0.3,1)_both]">
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-[#a83a4a] animate-ping opacity-75" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#a83a4a]" />
              </span>
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/70 font-body">Now on iOS &amp; Android</p>
            </div>

            <h1
              className="font-heading text-[3.2rem] sm:text-7xl lg:text-[5.4rem] text-white mb-6 leading-[0.92] tracking-[-0.02em] animate-[fadeUp_0.9s_cubic-bezier(0.16,1,0.3,1)_120ms_both]"
              data-testid="hero-title"
              style={{ textShadow: '0 2px 60px rgba(168,58,74,0.15)' }}
            >
              Take back<br />
              <span
                className="bg-gradient-to-r from-[#c44a5a] via-[#a83a4a] to-[#7a1f2b] bg-clip-text text-transparent"
                style={{ filter: 'drop-shadow(0 0 30px rgba(168,58,74,0.4))' }}
              >
                control.
              </span>
            </h1>

            <p className="text-white/55 text-base md:text-lg font-body max-w-md mb-9 leading-relaxed animate-[fadeUp_0.9s_cubic-bezier(0.16,1,0.3,1)_240ms_both]">
              The discreet recovery support app for men building discipline. Track your streak, master urges, and grow accountability — privately, in your pocket.
            </p>

            <div data-testid="hero-store-buttons" className="animate-[fadeUp_0.9s_cubic-bezier(0.16,1,0.3,1)_360ms_both]">
              <StoreButtons size="lg" />
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-7 text-white/30 text-xs font-body animate-[fadeUp_0.9s_cubic-bezier(0.16,1,0.3,1)_480ms_both]">
              <div className="flex items-center gap-1.5">
                <div className="flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-[#a83a4a] text-[#a83a4a]" />)}
                </div>
                <span>4.9 average</span>
              </div>
              <span className="text-white/15 hidden sm:inline">·</span>
              <span>7-day free trial</span>
              <span className="text-white/15 hidden sm:inline">·</span>
              <span>Cancel anytime</span>
            </div>
          </div>

          <div className="relative animate-[fadeUp_1.1s_cubic-bezier(0.16,1,0.3,1)_300ms_both]">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[340px] h-[340px] bg-[#a83a4a]/20 rounded-full blur-[100px]" />
            </div>
            <div className="relative">
              <PhoneFrame glowIntensity="lg">
                <RealScreen src="/screens/dashboard.jpg" alt="ResetProtocol streak dashboard — 4 days strong, Initiate level" />
              </PhoneFrame>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="relative z-10 border-y border-white/[0.06] bg-gradient-to-b from-[#0a0303]/80 via-[#050202]/40 to-[#0a0303]/80">
        <div className="container mx-auto max-w-4xl px-6 py-20 md:py-28 text-center">
          <Reveal><Logo size={56} glow className="mx-auto mb-6" /></Reveal>
          <Reveal delay={100}>
            <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.4em] font-body mb-5">The Protocol</p>
          </Reveal>
          <Reveal delay={200}>
            <h2 className="font-heading text-3xl md:text-5xl text-white mb-6 tracking-[-0.02em] leading-[1.05] max-w-3xl mx-auto">
              Built for the man who decided<br />
              <span className="text-[#a83a4a]">enough is enough.</span>
            </h2>
          </Reveal>
          <Reveal delay={300}>
            <p className="text-white/45 text-base md:text-lg font-body max-w-xl mx-auto leading-relaxed">
              Recovery support isn't soft. It's daily reps, hard truth, and a system that holds you when willpower won't. Reset gives you the structure.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SCREEN GALLERY — REAL SCREENSHOTS */}
      <section className="relative z-10 container mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="text-center mb-16 md:mb-20">
          <Reveal>
            <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.4em] font-body mb-4">Inside the app</p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-3xl md:text-5xl text-white tracking-[-0.02em] leading-[1.05] max-w-2xl mx-auto">
              Everything you need.<br />Nothing you don't.
            </h2>
          </Reveal>
        </div>

        {/* 3-phone hero gallery (real screenshots) */}
        <Reveal delay={200}>
          <div className="relative h-[640px] md:h-[700px] flex items-center justify-center mb-12">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[600px] h-[600px] bg-[#7a1f2b]/15 rounded-full blur-[120px]" />
            </div>

            <div className="absolute left-[3%] top-[8%] hidden md:block opacity-90">
              <PhoneFrame glowIntensity="sm" tilt={-8}>
                <RealScreen src="/screens/features.jpg" alt="Reset key features list" />
              </PhoneFrame>
            </div>

            <div className="relative z-10">
              <PhoneFrame glowIntensity="lg">
                <RealScreen src="/screens/milestones.jpg" alt="Reset streak milestones and AI coach" />
              </PhoneFrame>
            </div>

            <div className="absolute right-[3%] top-[12%] hidden md:block opacity-90">
              <PhoneFrame glowIntensity="sm" tilt={8}>
                <RealScreen src="/screens/armory.jpg" alt="Reset Armory — Titles, Frames, Badges" />
              </PhoneFrame>
            </div>
          </div>
        </Reveal>

        {/* Mobile fallback */}
        <div className="grid grid-cols-2 gap-4 md:hidden mb-10">
          <PhoneFrame glowIntensity="sm" className="!w-full">
            <RealScreen src="/screens/features.jpg" alt="Reset key features" />
          </PhoneFrame>
          <PhoneFrame glowIntensity="sm" className="!w-full">
            <RealScreen src="/screens/armory.jpg" alt="Reset Armory" />
          </PhoneFrame>
        </div>

        {/* Feature grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mt-8">
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.title} icon={f.icon} title={f.title} desc={f.desc} idx={i} />
          ))}
        </div>
      </section>

      {/* JOURNEY / ANALYTICS section using real analytics screenshot */}
      <section className="relative z-10 border-y border-white/[0.06] bg-gradient-to-b from-transparent via-[#0a0303]/40 to-transparent">
        <div className="container mx-auto max-w-6xl px-6 py-24 md:py-28">
          <div className="grid lg:grid-cols-[1fr_0.9fr] gap-16 items-center">
            <div className="order-2 lg:order-1 flex justify-center">
              <Reveal>
                <PhoneFrame glowIntensity="md">
                  <RealScreen src="/screens/analytics.jpg" alt="Recovery Analytics — check-ins, success rate, insights" />
                </PhoneFrame>
              </Reveal>
            </div>
            <div className="order-1 lg:order-2">
              <Reveal>
                <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.4em] font-body mb-4">The journey</p>
              </Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-3xl md:text-5xl text-white mb-6 tracking-[-0.02em] leading-[1.05]">
                  Track every<br />day back.
                </h2>
              </Reveal>
              <Reveal delay={200}>
                <p className="text-white/55 font-body text-base md:text-lg leading-relaxed mb-8 max-w-md">
                  13 ranks. 11 badges. 54 unlockables in the Armory. Every clean day moves you forward — visibly. The protocol turns recovery support into something you can actually see.
                </p>
              </Reveal>
              <Reveal delay={300}>
                <div className="flex flex-wrap gap-2.5">
                  {[
                    { i: Crown, l: 'Initiate' },
                    { i: Shield, l: 'Soldier' },
                    { i: Trophy, l: 'Operative' },
                    { i: Sparkles, l: 'Knight' },
                    { i: Flame, l: 'Spartan' },
                  ].map(({ i: Icon, l }, idx) => (
                    <div
                      key={l}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 border ${idx <= 2 ? 'bg-[#3a0e15]/40 border-[#a83a4a]/40 text-white/80' : 'bg-white/[0.02] border-white/10 text-white/30'}`}
                    >
                      <Icon className="w-3 h-3" strokeWidth={1.5} />
                      <span className="text-[10px] font-body uppercase tracking-[0.15em]">{l}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="relative z-10 container mx-auto max-w-6xl px-6 py-24 md:py-28">
        <div className="text-center mb-14">
          <Reveal>
            <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.4em] font-body mb-4">Trusted by</p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-3xl md:text-5xl text-white tracking-[-0.02em] leading-[1.05] max-w-2xl mx-auto">
              Men who refused<br />to lose another year.
            </h2>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto" data-testid="social-proof">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.n} delay={i * 100}>
              <blockquote className="relative p-7 bg-gradient-to-br from-white/[0.025] to-transparent border border-white/[0.06] h-full hover:border-[#a83a4a]/30 transition-all duration-500">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-[#a83a4a] text-[#a83a4a]" />)}
                </div>
                <p className="text-white/75 font-body text-[15px] leading-relaxed mb-5 italic">&ldquo;{t.q}&rdquo;</p>
                <p className="text-white/35 text-[10px] font-body uppercase tracking-[0.2em]">{t.n}</p>
              </blockquote>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 container mx-auto max-w-3xl px-6 py-20 md:py-24">
        <div className="text-center mb-14">
          <Reveal>
            <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.4em] font-body mb-4">Questions</p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-3xl md:text-5xl text-white tracking-[-0.02em]">Common questions</h2>
          </Reveal>
        </div>
        <Reveal>
          <div className="space-y-2" data-testid="faq-list">
            {FAQ.map((item, i) => (
              <FaqItem key={item.q} q={item.q} a={item.a} idx={i} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </div>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 container mx-auto max-w-4xl px-6 pb-28">
        <Reveal>
          <div className="relative border border-[#a83a4a]/30 bg-gradient-to-br from-[#3a0e15]/40 via-[#1a0507]/30 to-transparent p-10 md:p-16 text-center overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#a83a4a]/15 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative">
              <Logo size={48} glow className="mx-auto mb-6" />
              <h2 className="font-heading text-3xl md:text-5xl text-white mb-4 tracking-[-0.02em] leading-[1.05]">
                Day one starts now.
              </h2>
              <p className="text-white/45 font-body text-sm md:text-base mb-8 max-w-md mx-auto leading-relaxed">
                Download Reset and start the protocol. 7 days free. No catch. No commitment beyond yourself.
              </p>
              <div className="flex justify-center" data-testid="cta-store-buttons">
                <StoreButtons size="lg" />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
